import React from 'react';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import FilterListIcon from '@mui/icons-material/FilterList';
import { QcIssueStatus } from '../../../interfaces/qcIssue.interface';
import { QC_STATUS_RGB } from './qcTheme';
import { ASSESSOR_ACTION_LABEL, ASSESSOR_ACTION_STATUSES } from './workspaceDerivations';

// Filter toggles (order/labels per handoff); First Pass is admin-only, the assessor-owed pair shares a tile.
const TILE_ORDER: { key: string; statuses: QcIssueStatus[]; label: string; rgb: string }[] = [
  { key: 'first_pass', statuses: ['first_pass'], label: 'First Pass', rgb: QC_STATUS_RGB.first_pass },
  // The lighter header gray is deliberate: this tile is non-zero for most of a workflow's life.
  { key: 'assessor', statuses: ASSESSOR_ACTION_STATUSES, label: ASSESSOR_ACTION_LABEL, rgb: '128,134,144' },
  { key: 'ready_for_tpn_review', statuses: ['ready_for_tpn_review'], label: 'TPN Review', rgb: QC_STATUS_RGB.ready_for_tpn_review },
  { key: 'approved', statuses: ['approved'], label: 'Reviewed', rgb: QC_STATUS_RGB.approved },
];

const TilesBlock = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '6px',
  alignSelf: 'center',
  padding: '14px 24px',
  // Left rule only — a right rule read as a stray line beside the Continue QC Review modal's close button.
  borderLeft: '1px solid rgba(0,0,0,0.12)',
});

const TilesHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#424242',
  letterSpacing: '0.2px',
});

const HintIcon = styled(FilterListIcon)({
  fontSize: '15px',
  color: 'rgba(0,0,0,0.32)',
});

const TilesRow = styled('div')({
  display: 'flex',
  gap: '8px',
});

const Tile = styled('button', {
  shouldForwardProp: prop => !['rgb', 'on', 'zero'].includes(prop as string),
})<{ rgb: string; on: boolean; zero: boolean }>(({ rgb, on, zero }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  minWidth: '104px',
  padding: '8px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  fontFamily: 'inherit',
  background: on ? `rgba(${rgb},0.18)` : `rgba(${rgb},0.06)`,
  color: `rgb(${rgb})`,
  border: `1px solid ${on ? `rgba(${rgb},0.7)` : `rgba(${rgb},0.14)`}`,
  opacity: !zero || on ? 1 : 0.7,
  '&:hover': {
    filter: 'brightness(0.94)',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
    opacity: 1,
  },
}));

const TileCount = styled('span')({
  fontSize: '1.15rem',
  fontWeight: 700,
  lineHeight: 1,
});

const TileLabel = styled('span')({
  fontSize: '0.66rem',
  fontWeight: 600,
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
  textAlign: 'center',
  lineHeight: 1.2,
});

export interface QcStatusTilesProps {
  counts: Record<QcIssueStatus, number>;
  selected: QcIssueStatus[];
  showFirstPass: boolean;
  onToggle: (statuses: QcIssueStatus[]) => void;
}

export default function QcStatusTiles({ counts, selected, showFirstPass, onToggle }: QcStatusTilesProps): JSX.Element {
  const tiles = TILE_ORDER.filter(t => showFirstPass || t.key !== 'first_pass');
  const tileCount = (statuses: QcIssueStatus[]) => statuses.reduce((sum, status) => sum + counts[status], 0);
  const total = tiles.reduce((sum, t) => sum + tileCount(t.statuses), 0);
  return (
    <TilesBlock>
      <TilesHeader>
        QC Issues ({total})
        <Tooltip title='Click a status to filter'>
          <HintIcon />
        </Tooltip>
      </TilesHeader>
      <TilesRow>
        {tiles.map(tile => {
          // A grouped tile reads as on when either status is filtered, so the round's seeded default lights it.
          const on = tile.statuses.some(status => selected.includes(status));
          const count = tileCount(tile.statuses);
          return (
            <Tile
              key={tile.key}
              type='button'
              rgb={tile.rgb}
              on={on}
              zero={count === 0}
              aria-pressed={on}
              onClick={() => onToggle(tile.statuses)}
            >
              <TileCount>{count}</TileCount>
              <TileLabel>{tile.label}</TileLabel>
            </Tile>
          );
        })}
      </TilesRow>
    </TilesBlock>
  );
}
